import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, DollarSign, Database, Globe } from 'lucide-react';
import { Screen } from '../../types';

interface UseCasesScreenProps {
  screen: Screen;
}

const iconMap = {
  arrows: ArrowRight,
  briefcase: Briefcase,
  'dollar-sign': DollarSign,
  database: Database,
  globe: Globe
};

export const UseCasesScreen: React.FC<UseCasesScreenProps> = ({ screen }) => {
  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
        {/* Left Side - Use Cases */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2 space-y-8"
        >
          {/* Header */}
          <div className="space-y-4">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold text-black"
            >
              {screen.title}
            </motion.h1>
          </div>

          {/* Use Cases Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {screen.useCases?.map((useCase, index) => {
              const IconComponent = iconMap[useCase.icon as keyof typeof iconMap] || ArrowRight;
              
              return (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  whileHover={{ y: -2 }}
                  className="use-case-card"
                >
                  <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  
                  <h3 className="text-lg font-bold text-black mb-2">
                    {useCase.title}
                  </h3>
                  
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {useCase.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Right Side - Info Panel */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.subtitle}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {screen.description}
            </p>
          </div>

          {/* Additional Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="space-y-4"
          >
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Compliance Ready</h3>
              <p className="text-sm text-blue-700">
              Operate with confidence under EU MiCA standards — KYC/KYB, AML monitoring, and transaction reporting built-in
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2">Local Currency Settlement</h3>
              <p className="text-sm text-green-700">
              Settle instantly in PLN, EUR, and USD — built for PSPs, EMIs, and VASPs operating across Poland and the wider EU
              </p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-purple-900 mb-2">Simple Integration</h3>
              <p className="text-sm text-purple-700">
              Connect directly to your existing infrastructure, reducing time-to-market and operational overhead
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};
